import { z } from 'zod'
import type { TelErrorData } from './tel-error-data.js'
import type { Equal, Expect } from '@w/utils'
import { ulid } from '@w/utils'

// tel - telemetry. extends baggage so we can pass tel directly between services.
export interface Tel extends Baggage {
  // logs
  info: (message: string, data?: Pick<LogData, 'attributes'>) => void
  error: (message: string, data?: Pick<LogData, 'attributes'>) => void
  warn: (message: string, data?: Pick<LogData, 'attributes'>) => void
  debug: (message: string, data?: Pick<LogData, 'attributes'>) => void

  // events
  createEvent(event: CreateEventData): void
  createEvent(key: string, cb: (old?: CreateEventData) => CreateEventData): void

  // error event
  onCatch<T>(error: T, extra?: { componentStack?: string }): T // span can have multiple (log) errors associated with it. When it ends it can succeed or end with another error.

  // traces
  startSpan<R>(name: string, cb: (tel: Tel) => R): R
  startSpan<R>(opts: StartSpanOpts, cb: (tel: Tel) => R): R
  startSpan<I, R>(name: string, cb: (input: I, tel: Tel) => R, input: I): R
  startSpan<I, R>(opts: StartSpanOpts, cb: (input: I, tel: Tel) => R, input: I): R

  // db
  startDbQuery<T>(
    name: string,
    query: { toSQL: () => { sql: string; params: unknown[] }; execute: () => Promise<T> },
    opts?: DbSpanOpts,
  ): Promise<T>
  startDbMutation<T>(
    name: string,
    query: { toSQL: () => { sql: string; params: unknown[] }; execute: () => Promise<T> },
    opts?: DbSpanOpts,
  ): Promise<T>

  flush(): Promise<void>

  _updateSpan(patch: Partial<SpanData>): void

  _createSpan(opts: StartSpanOpts): Span
  _createRequestSpan(opts: StartSpanOpts): Span // same as createSpan but requestId is set
  _createTel(opts: StartSpanOpts): Tel // same as createSpan but returns tel

  _registerFlush(cb: () => Promise<void>): void

  _clear(): void

  span: Span
}

export type StartSpanOpts = Pick<
  SpanData,
  'name' | 'type' | 'input' | 'parentId' | 'traceId' | 'target' | 'label' | 'requestId'
> & {
  id?: Ulid
  /**
   * Overrides the service-wide `captureSpanOutput` for THIS span, in either
   * direction. A directive, not span data: it never reaches SpanData, so it is
   * never exported.
   *
   * Capture is fail-closed, so `true` here is the deliberate opt-in — one span
   * at a time, for an operation whose result is known to carry no customer data
   * and is worth seeing while debugging. `false` is how a store pins the
   * guarantee for its own spans regardless of what the HOST service configured.
   *
   * It does NOT inherit. A child span opened inside this one gets the
   * service-wide policy again unless it says otherwise itself; only the span
   * this opts was passed for is affected.
   */
  captureOutput?: boolean
}

/**
 * What a caller may steer about the db span these helpers open on its behalf.
 *
 * `captureOutput` is the reason this exists at all: a db span is where output
 * capture matters most — rows ARE the customer data — and until now the only
 * way to state a policy for one query was to stop using `startDbQuery` and open
 * the span by hand. A store that must never log its rows whatever its HOST
 * configured passes `false` here; someone debugging one query passes `true`.
 *
 * It does not inherit: `builder.execute()` runs inside this span, but any span
 * opened underneath gets the service-wide policy again.
 */
export type DbSpanOpts = Pick<StartSpanOpts, 'captureOutput'>

type Ulid = string

export interface Span {
  data: SpanData
  // attributes will be merged with existing attributes
  end(
    data: Pick<SpanData, 'output' | 'attributes'> & { error?: unknown; name?: string },
    opts?: { errorLevel: 'warning' | 'error' },
  ): void
  update(patch: Partial<SpanData>): void

  /**
   * Whether an output handed to this span would be RECORDED, rather than
   * gated down to its volume and size.
   *
   * For producers that have to build the output before they can report it —
   * cloning and parsing an HTTP response body, extreme-masking a third party's
   * JSON. Capture is fail-closed, so answering `false` is the common path and
   * asking first is what stops the middleware doing that work for a value the
   * gate is about to drop. It is the same question the gate asks, so the two
   * cannot disagree.
   */
  capturesOutput(): boolean

  _getBaggage(): Baggage
}

export const _originArray = ['server', 'client'] as const
export type Origin = (typeof _originArray)[number]

export const _spanTypeArray = ['query', 'mutation', 'subscription'] as const
export type SpanType = (typeof _spanTypeArray)[number]
export const _spanTargetArray = [
  'db',
  'bucket',
  'fetch',
  'trpc-handler',
  'server-handler',
  'job',
  'router',
  '3rdParty',
] as const
export type SpanTarget = (typeof _spanTargetArray)[number]
export const _spanStatusArray = ['ok', 'error', 'warning'] as const
export type SpanStatus = (typeof _spanStatusArray)[number]

// these props are inherited and propagated
export interface Baggage {
  parentId?: Ulid // same as otel spanId, for logs and events it is their spanId
  traceId?: Ulid // flexible traceId, you can set so one traceId is limited to a single service or team
  requestId?: Ulid // requests start on frontend and propagate through backend. This is traceId for grafana (since it lacks proper fronted integrations). can be sent to sentry to link it to grafana.
  sessionId?: Ulid // links all spans to user information and client version (eg client might be outdated and it can cause errors)
}

// set on rootSpan only
export interface ResourceUserInfo {
  userAgent?: UserAgent
  userId?: string
  entityUserId?: string // similar to userId
  companyId?: string
  entityId?: string // similar to companyId
}

export type TelParent = Omit<Baggage, 'parentId'> & { id?: string }
// spans describe an asynchronous operation (its duration, input, output, error). spans are foundation of observability.
export interface SpanData extends Baggage, ResourceUserInfo {
  previousId?: Ulid // when request travels through a system traces can be terminated and linked using this field
  // nextId?: Ulid // kinda makes no sense. use previousId // when user navigates between different pages in one session link them using these two fields. first navigation links to session span

  // set on boundary spans only. 1 execution context (micro-frontend or microservice) has 1 resource.
  // resourceId?: string // too much head ache with separately managed resources.
  resourceName?: string // assume that it is unique
  resourceVersion?: string // every deploy new version is created

  id: Ulid
  name: string // ideally it should be low cardinality
  type?: SpanType
  displayName?: string // high cardinality name eg inlinePath(routePath, params)

  performanceMetricId?: Ulid

  label?: string // eg 3rd party service name, or a unique key to track apdex
  target?: SpanTarget

  // automatically set
  origin?: Origin
  worker?: boolean

  duration?: number

  input?: unknown
  inputSize?: number
  output?: unknown
  outputSize?: number
  error?: TelErrorData

  status?: SpanStatus

  attributes?: Record<string, unknown>

  _exported?: boolean // when tail based sampling is used we need to always export error spans because there might be risk that when we will be exporting a trace the span is removed from the memory. and we don't want to double export
}
export function getBaggage(span: SpanData): Baggage {
  return {
    traceId: span.traceId,
    requestId: span.requestId,
    sessionId: span.sessionId,
    parentId: span.id,
  }
}
export function getLogBaggage(span: SpanData): Baggage {
  return {
    ...getBaggage(span),
    parentId: span.id,
  }
}

// spans can have logs. logs provide additional information about asynchronous operations.
export interface LogData extends Baggage {
  id: Ulid
  message: string

  level: LogLevel

  origin?: Origin
  worker?: boolean

  data?: unknown
  error?: TelErrorData

  attributes?: Record<string, unknown>
}

export const _logLevelArray = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'] as const
export type LogLevel = (typeof _logLevelArray)[number]

// some systems (eg react-query) cannot provide spans. span events are used to describe their operations.
// in span events parentId is spanId.
export interface SpanEventData extends Baggage {
  id: Ulid
  name: string

  key?: string
  data?: unknown
  duration?: number

  origin?: Origin
  worker?: boolean

  target?: 'react-query' | (string & {})

  attributes?: Record<string, unknown>
}

export type CreateEventData = Omit<SpanEventData, 'id'> & { id?: Ulid }

export interface TraceData extends SpanData {
  events: SpanEventData[]
  logs: LogData[]
  children: TraceData[]
}

// Type that removes 'id' from telemetry data types
type WithoutId<T> = Omit<T, 'id'>

// Type for creating TraceData with proper ID stripping
type CreateTraceDataInput = Omit<TraceData, 'events' | 'logs' | 'children'> & {
  events?: WithoutId<SpanEventData>[]
  logs?: WithoutId<LogData>[]
  children?: CreateTraceDataInput[]
}

export function _createFakeTraceData(overrides: Partial<CreateTraceDataInput> = {}): TraceData {
  const { events = [], logs = [], children = [], ...spanDataOverrides } = overrides

  // Create base span data with overrides first to get the span ID
  const baseSpanData: SpanData = {
    id: ulid(),
    name: '',
    traceId: spanDataOverrides.traceId || ulid(),

    ...spanDataOverrides,
  }

  // Add 'id' and propagate baggage to events
  const processedEvents: SpanEventData[] = events.map((eventData) => ({
    ...eventData,
    id: ulid(), // Generate new id
    parentId: baseSpanData.id,
    traceId: baseSpanData.traceId,
    requestId: baseSpanData.requestId,
    sessionId: baseSpanData.sessionId,
  }))

  // Add 'id' and propagate baggage to logs
  const processedLogs: LogData[] = logs.map((logData) => ({
    ...logData,
    id: ulid(), // Generate new id
    parentId: baseSpanData.id,
    traceId: baseSpanData.traceId,
    requestId: baseSpanData.requestId,
    sessionId: baseSpanData.sessionId,
  }))

  // Process children recursively and associate with the span, propagating baggage
  const processedChildren: TraceData[] = children.map((child) => {
    return _createFakeTraceData({
      ...child,
      parentId: baseSpanData.id, // Associate with parent span
      traceId: baseSpanData.traceId,
      requestId: baseSpanData.requestId,
      sessionId: baseSpanData.sessionId,
    })
  })

  return {
    ...baseSpanData,
    events: processedEvents,
    logs: processedLogs,
    children: processedChildren,
  }
}

// to explicitly propagate baggage
export const telSchema = z
  .object({
    parentId: z.string().optional(), // in tests these fields are optional
    traceId: z.string().optional(),
    requestId: z.string().optional(),
    sessionId: z.string().optional(),
  })
  .optional()

type TelSchema = z.infer<typeof telSchema>

type _Guard = Expect<Equal<keyof NonNullable<TelSchema>, keyof Baggage>>

export type WithTel = { tel?: TelSchema }

export const userAgentSchema = z.object({
  browser: z.object({ name: z.string().optional(), version: z.string().optional() }),
  engine: z.object({ name: z.string().optional(), version: z.string().optional() }),
  os: z.object({ name: z.string().optional(), version: z.string().optional() }),
})

export type UserAgent = z.infer<typeof userAgentSchema>
